import React, { useEffect, useState } from "react";
import { createNotificationApi } from "../../notifications/notificationApi";

const TaskWatchPanel = ({ task, apiUrl, getToken, role, assignOptions = [] }) => {
    const api = createNotificationApi({ apiUrl, getToken });
    const [watchers, setWatchers] = useState([]);
    const [watching, setWatching] = useState(false);
    const [busy, setBusy] = useState(false);
    const isStaff = role === "staff";

    const load = async () => {
        if (!task?.id) return;
        try {
            const result = await api.listWatchers(task.id);
            setWatchers(result.data || []);
            setWatching(Boolean(result.watching));
        } catch {
            setWatchers([]);
            setWatching(false);
        }
    };

    useEffect(() => {
        load();
    }, [task?.id]);

    const toggleSelf = async () => {
        setBusy(true);
        try {
            if (watching) await api.unwatch(task.id);
            else await api.watch(task.id);
            await load();
        } finally {
            setBusy(false);
        }
    };

    const addWatcher = async (staffId) => {
        if (!staffId) return;
        setBusy(true);
        try {
            await api.watch(task.id, staffId);
            await load();
        } finally {
            setBusy(false);
        }
    };

    const removeWatcher = async (staffId) => {
        setBusy(true);
        try {
            await api.unwatch(task.id, staffId);
            await load();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-4 rounded-xl border border-line bg-surface-2 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-xs font-semibold text-ink">Watchers</p>
                {isStaff && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={toggleSelf}
                        className="rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-3 disabled:opacity-50"
                    >
                        {watching ? "Unwatch" : "Watch this task"}
                    </button>
                )}
            </div>
            {watchers.length === 0 ? (
                <p className="text-[11px] text-ink-muted">No watchers yet. Mention someone with @Name or add them here.</p>
            ) : (
                <ul className="space-y-1">
                    {watchers.map((w) => (
                        <li key={w.id} className="flex items-center justify-between text-xs text-ink">
                            <span>
                                {`${w.firstName || ""} ${w.lastName || ""}`.trim() || `Staff #${w.id}`}
                            </span>
                            {!isStaff && (
                                <button type="button" disabled={busy} onClick={() => removeWatcher(w.id)}>
                                    Remove
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {!isStaff && assignOptions.length > 0 && (
                <select
                    className="mt-2 w-full rounded-lg border border-line bg-surface px-2 py-1 text-xs"
                    defaultValue=""
                    disabled={busy}
                    onChange={(e) => {
                        addWatcher(e.target.value);
                        e.target.value = "";
                    }}
                >
                    <option value="">Add watcher…</option>
                    {assignOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
};

export default TaskWatchPanel;
